import type { SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Spacer, Typography, useToast } from "@aviary";
import FullscriptLogoSvg from "@shared/assets/logo/fullscript-logo-green-leaf-pigeon-text.svg";
import WellevateByFullscriptLogo from "@shared/assets/logo/wellevate-by-fullscript.png";
import { EmailInput } from "@shared/components";
import { useWellevateAccountActivation } from "@shared/data/mutations";
import { useBreakpoints } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils";
import {
  FormWrapper,
  isPasswordValid,
  PasswordInput,
  SignInWrapper,
} from "@unauthenticated/shared/components";
/* eslint-disable @fullscript/cross-reference */
import {
  SignUpModalHeader,
  SignUpModalTitle,
  StoreLogo,
  StoreOffersPill,
} from "@unauthenticated/signupSignin/components";
import { usePatientSignUpMutation } from "@unauthenticated/signupSignin/data/mutations";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
/* eslint-enable @fullscript/cross-reference */
import { l } from "@unauthenticated/wellevateAuthorize/patients/locales/i18n";

import * as styles from "../WellevateSignInSignUp.styles";
import { WellevateStepWrapper } from "../WellevateStepWrapper";

interface Props {
  patientAttributes: PatientAttributes;
  pageVersion: string;
}

const WellevateStepSignUp = ({ patientAttributes, pageVersion }: Props) => {
  const { tablet } = useBreakpoints();
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);
  const { search } = useLocation();
  const { makeToast } = useToast();

  const query = new URLSearchParams(search);
  const token = query.get("token");

  const isFormValid = isPasswordValid(password);

  const { storeLogo, storeName, pracName, email, treatmentPlanSlug, storeSlug, uuid } =
    patientAttributes;

  const [activateWellevateAccount, { loading: wellevateAccountActivationLoading }] =
    useWellevateAccountActivation({
      onCompleted: () => window.location.assign("/"),
      onError: ({ message }) => makeToast("error", message),
    });

  const [signUp, { loading: signUpMutationLoading, data: signUpMutationData }] =
    usePatientSignUpMutation({
      onCompleted: () => {
        activateWellevateAccount({
          variables: {
            input: {
              token,
              activate: true,
            },
          },
        });
      },
    });

  const errors = signUpMutationData?.auth?.patientSignUpFromRx?.errors?.fields;

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      setPasswordDirty(true);

      return;
    }

    gRecaptchaExecute(gToken => {
      const attributes = {
        treatmentPlanSlug,
        storeSlug,
        uuid,
        password,
        pageVersion,
        signUpPage: "wellevate_patient_sign_up_page",
        formVersion: "WellevateMigration",
        optional: {
          source: "wellevate",
        },
      };

      signUp({ variables: { input: { attributes, captchaToken: gToken } } });
    });
  };

  const headerText = t(l.common.signUpHeader.title, {
    practitionerName: pracName,
  });

  const bodyText = t(l.common.signUpHeader.content, {
    practitionerName: pracName,
  });

  return (
    <WellevateStepWrapper
      headerText={headerText}
      bodyText={bodyText}
      bodySubText={t(l.common.signUpHeader.subContent)}
    >
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        {tablet.lessThan && (
          <section css={styles.section}>
            <div css={styles.logos}>
              <FullscriptLogoSvg css={styles.logo} />
              <img
                src={WellevateByFullscriptLogo}
                css={styles.logo}
                alt={t(l.companies.Wellevate)}
              />
            </div>
            <Typography type="h2" sizeOverride="h4">
              {headerText}
            </Typography>
            <Typography>{bodyText}</Typography>
          </section>
        )}
      </SignUpModalHeader>
      <FormWrapper>
        <SignInWrapper>
          <form onSubmit={handleSubmit} css={styles.form} noValidate>
            <EmailInput value={email} setEmail={null} disabled isDirty={false} hideSuggestion />
            <Spacer height="one" />
            <PasswordInput
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              isDirty={passwordDirty}
              onFocus={() => setPasswordDirty(true)}
              label={t(l.patientRxAuth.CreatePassword)}
              errors={errors?.password}
              isLoading={false}
            />
            <Spacer height="one" />
            <Button
              type="submit"
              isFullWidth
              disabled={!isFormValid}
              isLoading={signUpMutationLoading || wellevateAccountActivationLoading}
            >
              {t(l.common.CreateMyAccount)}
            </Button>
          </form>
        </SignInWrapper>
      </FormWrapper>
    </WellevateStepWrapper>
  );
};

export { WellevateStepSignUp };
